import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getEtablissementTheme } from '../api/djangoApi';

const ThemeContext = createContext(null);

// Couleurs par défaut SchoolSense — Sidebar Variant A (harmonie canonique)
const DEFAULT_COLORS = {
  primary: '#2F2F2F',
  'primary-light': '#3B82F6',
  'primary-dark': '#1E40AF',
  accent: '#F97316',
  'accent-light': '#FDBA74',
  'accent-dark': '#EA580C',
  secondary: '#10B981',
  'secondary-light': '#6EE7B7',
  'secondary-dark': '#059669',
  'sidebar-bg': '#FAFAF8',
  'sidebar-active': '#F97316',
  'sidebar-text': '#4B5563',
  'sidebar-separator': '#EDE9E3',
  'sidebar-hover': 'rgba(249, 115, 22, 0.07)',
  'sidebar-icon': '#9CA3AF',
  'sidebar-section': '#B0A99E',
  'sidebar-user-bg': '#F4F1ED',
  'hover-bg': 'rgba(249, 115, 22, 0.07)',
  'button-primary': '#2F2F2F',
  'button-primary-hover': '#1a1a1a',
  'header-bg': '#ffffff',
};

const DEFAULT_DARK_COLORS = {
  primary: '#e8edf4',
  'primary-light': '#60a5fa',
  'primary-dark': '#1d4ed8',
  accent: '#fb923c',
  'accent-light': '#fdba74',
  'accent-dark': '#f97316',
  secondary: '#22c55e',
  'secondary-light': '#86efac',
  'secondary-dark': '#16a34a',
  'sidebar-bg': '#0f172a',
  'sidebar-active': '#fb923c',
  'hover-bg': 'rgba(255,255,255,0.06)',
  'button-primary': '#e8edf4',
  'button-primary-hover': '#f3f4f6',
  'header-bg': '#111827',
};

// Génère une gamme complète (50-900) à partir d'une couleur de base
function generateColorScale(hex) {
  if (!hex) return null;
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);

  // Convertir en HSL
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  let h, s, l = (max + min) / 2;

  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r1: h = ((g1 - b1) / d + (g1 < b1 ? 6 : 0)) / 6; break;
      case g1: h = ((b1 - r1) / d + 2) / 6; break;
      default: h = ((r1 - g1) / d + 4) / 6; break;
    }
  }

  const hslToHex = (hue, sat, lig) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    let rv, gv, bv;
    if (sat === 0) { rv = gv = bv = lig; }
    else {
      const q2 = lig < 0.5 ? lig * (1 + sat) : lig + sat - lig * sat;
      const p2 = 2 * lig - q2;
      rv = hue2rgb(p2, q2, hue + 1/3);
      gv = hue2rgb(p2, q2, hue);
      bv = hue2rgb(p2, q2, hue - 1/3);
    }
    const toHex = (v) => Math.round(Math.min(255, Math.max(0, v * 255))).toString(16).padStart(2, '0');
    return `#${toHex(rv)}${toHex(gv)}${toHex(bv)}`;
  };

  // Lightness stops pour chaque palier (50=très clair → 900=très foncé)
  return {
    50:  hslToHex(h, Math.min(1, s * 1.1), 0.96),
    100: hslToHex(h, Math.min(1, s * 1.05), 0.91),
    200: hslToHex(h, s, 0.82),
    300: hslToHex(h, s, 0.70),
    400: hslToHex(h, Math.min(1, s * 0.95), 0.58),
    500: hslToHex(h, s, l),   // La couleur de base
    600: hslToHex(h, Math.min(1, s * 1.05), Math.max(0.15, l * 0.78)),
    700: hslToHex(h, Math.min(1, s * 1.1), Math.max(0.12, l * 0.60)),
    800: hslToHex(h, Math.min(1, s * 1.1), Math.max(0.10, l * 0.45)),
    900: hslToHex(h, Math.min(1, s * 1.05), Math.max(0.08, l * 0.32)),
  };
}

function applyThemeToDOM(config, isDark) {
  const root = document.documentElement;
  const mode = isDark ? 'dark' : 'light';
  const defaults = isDark ? DEFAULT_DARK_COLORS : DEFAULT_COLORS;
  const colors = config?.[mode] || {};

  // Appliquer les CSS custom properties
  const vars = { ...defaults, ...colors };
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key}`, value);
  });

  // Mettre à jour les tokens sémantiques existants si les couleurs sont personnalisées
  if (colors.primary) {
    root.style.setProperty('--color-primary', colors.primary);
  }
  if (colors['primary-light']) {
    root.style.setProperty('--color-primary-light', colors['primary-light']);
  }
  if (colors['primary-dark']) {
    root.style.setProperty('--color-primary-dark', colors['primary-dark']);
  }
  if (colors.accent) {
    root.style.setProperty('--color-accent', colors.accent);
  }
  if (colors['accent-light']) {
    root.style.setProperty('--color-accent-light', colors['accent-light']);
  }
  if (colors['accent-dark']) {
    root.style.setProperty('--color-accent-dark', colors['accent-dark']);
  }
  if (colors['sidebar-bg'] && !isDark) {
    root.style.setProperty('--sidebarBg', colors['sidebar-bg']);
  }
  // Variant A sub-tokens — keep structural harmony in sync with the active color
  const sidebarSubTokens = [
    ['sidebar-text', '--sidebarText'],
    ['sidebar-separator', '--sidebarSeparator'],
    ['sidebar-hover', '--sidebarHover'],
    ['sidebar-active', '--sidebarActive'],
    ['sidebar-icon', '--sidebarIcon'],
    ['sidebar-section', '--sidebarSection'],
    ['sidebar-user-bg', '--sidebarUserBg'],
  ];
  sidebarSubTokens.forEach(([src, dst]) => {
    if (colors[src]) root.style.setProperty(dst, colors[src]);
  });

  // Générer la gamme complète orange à partir de l'accent
  const accentColor = colors.accent || defaults.accent;
  if (accentColor) {
    const scale = generateColorScale(accentColor);
    if (scale) {
      Object.entries(scale).forEach(([stop, value]) => {
        root.style.setProperty(`--tw-orange-${stop}`, value);
      });
    }
  }
}

function clearThemeFromDOM() {
  const root = document.documentElement;
  const allKeys = [...new Set([...Object.keys(DEFAULT_COLORS), ...Object.keys(DEFAULT_DARK_COLORS)])];
  allKeys.forEach(key => root.style.removeProperty(`--theme-${key}`));
  // Rétablir les tokens sémantiques par défaut
  root.style.removeProperty('--color-primary');
  root.style.removeProperty('--color-primary-light');
  root.style.removeProperty('--color-primary-dark');
  root.style.removeProperty('--color-accent');
  root.style.removeProperty('--color-accent-light');
  root.style.removeProperty('--color-accent-dark');
  root.style.removeProperty('--sidebarBg');
  root.style.removeProperty('--sidebarText');
  root.style.removeProperty('--sidebarSeparator');
  root.style.removeProperty('--sidebarHover');
  root.style.removeProperty('--sidebarActive');
  root.style.removeProperty('--sidebarIcon');
  root.style.removeProperty('--sidebarSection');
  root.style.removeProperty('--sidebarUserBg');
  // Clear orange scale
  [50, 100, 200, 300, 400, 500, 600, 700, 800, 900].forEach(stop => {
    root.style.removeProperty(`--tw-orange-${stop}`);
  });
}

// Préfixes de routes authentifiées où le thème de l'établissement doit s'appliquer.
// Toute autre route (page vitrine "/", pages marketing, /demo, /login, /etablissement/:slug,
// /inscription/..., /invite/..., etc.) reste sur les couleurs par défaut de SchoolSense.
const THEMED_ROUTE_PREFIXES = [
  '/directeur',
  '/prof',
  '/eleve',
  '/parent',
  '/admin',
  '/secretariat',
  '/vie-scolaire',
  '/comptabilite',
  '/rh',
  '/infirmerie',
];

function isThemedRoute(pathname) {
  if (!pathname) return false;
  return THEMED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

// Petit composant à monter à l'intérieur du <BrowserRouter> : il informe le
// ThemeProvider du pathname courant pour n'appliquer les couleurs
// personnalisées que sur les pages internes à l'établissement.
export function ThemeRouteSync() {
  const ctx = useContext(ThemeContext);
  const location = useLocation();
  useEffect(() => {
    if (ctx && typeof ctx._setPathname === 'function') {
      ctx._setPathname(location.pathname);
    }
  }, [ctx, location.pathname]);
  return null;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [themeData, setThemeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pathname, setPathname] = useState(
    () => (globalThis.window ? globalThis.window.location.pathname : '/')
  );

  const etabId = user?.etablissement || null;
  const userRole = user?.role || null;

  // Vérifie si le thème doit s'appliquer à l'utilisateur courant
  const shouldApplyTheme = useCallback((data) => {
    if (!data?.theme_primary) return false;
    const scope = data.theme_scope || 'global';
    if (scope === 'global') return true;
    // Mode ciblé : directeur voit toujours le thème
    if (userRole === 'directeur') return true;
    const targetRoles = data.theme_target_roles || [];
    return targetRoles.includes(userRole);
  }, [userRole]);

  // Charger le thème au login / changement d'établissement
  useEffect(() => {
    if (!etabId) {
      setThemeData(null);
      clearThemeFromDOM();
      return;
    }

    let cancelled = false;
    setLoading(true);

    getEtablissementTheme(etabId)
      .then((data) => {
        if (!cancelled) {
          setThemeData(data);
          // Persister en localStorage pour chargement instantané
          try {
            localStorage.setItem(`theme_${etabId}`, JSON.stringify(data));
          } catch { /* quota */ }
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Essayer le cache local
          try {
            const cached = localStorage.getItem(`theme_${etabId}`);
            if (cached) setThemeData(JSON.parse(cached));
          } catch { /* noop */ }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [etabId]);

  // Appliquer le thème au DOM quand les données changent.
  // ⚠️ Ne s'applique QUE sur les routes internes à l'établissement
  // (dashboards des profils). La page vitrine et les pages publiques
  // conservent la charte SchoolSense par défaut.
  useEffect(() => {
    const onThemedRoute = isThemedRoute(pathname);
    if (
      onThemedRoute &&
      themeData?.theme_config &&
      Object.keys(themeData.theme_config).length > 0 &&
      shouldApplyTheme(themeData)
    ) {
      const isDark = document.documentElement.classList.contains('theme-dark');
      applyThemeToDOM(themeData.theme_config, isDark);
    } else {
      clearThemeFromDOM();
    }
  }, [themeData, shouldApplyTheme, pathname]);

  // Réappliquer quand le mode light/dark change
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const onThemedRoute = isThemedRoute(pathname);
      if (
        onThemedRoute &&
        themeData?.theme_config &&
        Object.keys(themeData.theme_config).length > 0 &&
        shouldApplyTheme(themeData)
      ) {
        const isDark = document.documentElement.classList.contains('theme-dark');
        applyThemeToDOM(themeData.theme_config, isDark);
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [themeData, shouldApplyTheme, pathname]);

  const refreshTheme = useCallback(async () => {
    if (!etabId) return;
    try {
      const data = await getEtablissementTheme(etabId);
      setThemeData(data);
      try {
        localStorage.setItem(`theme_${etabId}`, JSON.stringify(data));
      } catch { /* quota */ }
    } catch { /* noop */ }
  }, [etabId]);

  const value = useMemo(() => ({
    themeData,
    loading,
    refreshTheme,
    logoUrl: themeData?.logo_url || null,
    hasCustomTheme: !!(themeData?.theme_primary),
    themeScope: themeData?.theme_scope || 'global',
    themeTargetRoles: themeData?.theme_target_roles || [],
    isThemeApplied: shouldApplyTheme(themeData) && isThemedRoute(pathname),
    // Interne : permet à <ThemeRouteSync/> de remonter le pathname courant.
    _setPathname: setPathname,
  }), [themeData, loading, refreshTheme, shouldApplyTheme, pathname]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
